import { FC, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  formattedCurrency,
  InteracBulkPayoutTransactionResponse,
  withStyles,
  capitalizeFirstLetter,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus, ExpandableText } from 'UI';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransactionApproval from './useTransactionApproval';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type InteracBulkPayoutTransactionInfoProps = {
  transaction: InteracBulkPayoutTransactionResponse;
  Notes: string;
  isParent?: boolean;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

const InteracBulkPayoutTransactionInfo: FC<InteracBulkPayoutTransactionInfoProps> = ({
  transaction,
  Notes,
  isParent = false,
}) => {
  const toast = useToast();
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InteracBulkPayoutTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();

  const isApprovalRequested = useMemo(() => {
    return allApprovalTransactions.some((t) => t.ID.toString() === transaction.TransactionID);
  }, [transaction.TransactionID, allApprovalTransactions]);

  useEffect(() => {
    if (!isApprovalRequested) return;

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transaction.TransactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [isApprovalRequested]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Date Time' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('TransactionDetails.interacBulkPayout', { defaultValue: 'Interac Bulk Payout' }),
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
  ];

  const approvalHistoryDetails =
    approvalHistory.length > 0
      ? approvalHistory.flatMap((item) => [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: item.Username,
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: (
              <Box flx="col" gapSize="11px" ai="fe">
                <TransactionStatus
                  status={item.ApprovalDecision.toLowerCase()}
                  label={capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                      defaultValue: item.ApprovalDecision,
                    }),
                  )}
                />
                <div>{item.DecisionDate}</div>
              </Box>
            ),
          },
          {
            label: t('ApprovalHistoryDetails.reason', { defaultValue: 'Reason' }),
            value: <ExpandableText text={item.Reason || ''} />,
            visible: item.Reason !== null,
          },
        ])
      : [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: '-',
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: '-',
          },
        ];

  const eTransferDetails = [
    {
      label: t('ETransferDetails.email', { defaultValue: 'Email' }),
      value: transaction.EmailAddress || '-',
    },
    {
      label: t('ETransferDetails.phone', { defaultValue: 'Phone' }),
      value: transaction.PhoneNumber || '-',
    },
    {
      label: t('ETransferDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value: transaction.RecipientName || '-',
    },
    {
      label: t('ETransferDetails.confirmationNumber', { defaultValue: 'Confirmation Number' }),
      value: transaction.ConfirmationNumber || '-',
    },
    {
      label: t('ETransferDetails.messageForRecipient', { defaultValue: 'Message For Recipient' }),
      value: transaction.Memo || '-',
    },
    {
      label: t('ETransferDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
    {
      label: t('ETransferDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.RecipientName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          status={transaction.TransactionStatus}
          type={'Interac Bulk Payout'}
          transactionDate={transaction.TransactionDateTime}
          lastModified={transaction.LastModified}
          failureReason={transaction.FailureReason}
          isApprovalRequested={isApprovalRequested}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {isApprovalRequested && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('ApprovalHistoryDetails.title', { defaultValue: 'Approval History' })}
          </Title>
          <DetailsRows className={cn('rows')} details={approvalHistoryDetails} />
        </Card>
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('eTransferDetailsTitle', { defaultValue: 'e-Transfer Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={eTransferDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default InteracBulkPayoutTransactionInfo;
